import { AxiosResponse } from "axios";
import moment from "moment";
import { PXPJossoInfoResponse } from "p6m-aea-networking";
import { GeneralContext } from "p6m-contexts";
import { TeacherResponse, UserInfo } from "p6m-p6u";
import * as Sentry from "@sentry/react";
import React, { useState } from "react";
import { useCookies } from "react-cookie";
import { useHistory } from "react-router-dom";
import { CookieSetOptions } from "universal-cookie/cjs/types";
import { cp } from "../config";
import type { AssistantHandoffState } from "../@types/anonymousFlow";
import { CreationModes } from "../constants/CreationFlowConstants";
import {
    generateUuid,
    getRequest,
    pxpDeleteRequest,
    pxpGetRequest,
    pxpPostRequest,
    sessionGetRequest,
} from "../helpers/networkHelper";
import type { SsoProvider } from "../constants/SsoConstants";
import { setUserGroup, TEACHER_CONTACT_GROUP } from "../networking/registration";
import { setSentryRole } from "../sentry";
import { hasAllowedRole } from "../utils/userUtils";

const AppGeneralContext = React.createContext<GeneralContext | undefined>(undefined);

export type MakePxpRequest = (method: "GET" | "POST" | "DELETE", endpoint: string, params?: {}) => Promise<any>;

/*
 * Function instead of a constant so cp.cfg is evaluated lazily at call time,
 * not at module load time when cp.cfg is still undefined.
 */
const getAvatarUrlFallback = () =>
    `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}/system/galleries/pics/avatars/p6-mobile-classic-avatar-grey-darkxl.png`;

const _isTeacherRole = (userInfo?: UserInfo) => userInfo?.visitorOrUserRole?.toLowerCase() === "teacher";

// SSO-registered teachers (Google/Apple Sign-in) may have no name; the backend can then omit the
// name keys or send them empty. Normalise here so downstream code can rely on plain strings.
const _normalizeTeacherInfo = (raw: TeacherResponse): TeacherResponse => ({
    ...raw,
    salutation: (raw.salutation ?? "").trim(),
    firstname: (raw.firstname ?? "").trim(),
    lastname: (raw.lastname ?? "").trim(),
});

interface PxpSessionTokens {
    clientId: string;
    pxpAuthToken: string;
    userEmail: string;
    userId: string;
}

// Same rule as the website's onboarding: an account with one of these roles has finished a registration.
const STATUS_ROLES = ["parent", "student", "adult", TEACHER_CONTACT_GROUP];

/**
 * Loads the JOSSO session's user-info without touching state, so loginUserAfterSso can read the roles before the
 * login: setting userId that early would let AnonymousTestClaimer start before the account is ready.
 */
const _fetchSessionInfo = async (sessionId?: string): Promise<PXPJossoInfoResponse> => {
    if (!sessionId) throw new Error("Session id is missing");
    const request = (): Promise<AxiosResponse<PXPJossoInfoResponse>> =>
        sessionGetRequest("/user-info", { sessionId }, {}, { timeout: 5000 });
    // Retry once more in case it failed
    const response = await request().catch(() => request());
    return response.data;
};

const _loadHasVocabTraining = async (args: PxpSessionTokens): Promise<boolean | null> => {
    try {
        const response = await pxpGetRequest("cardsTestList", {
            "X-CLIENTID": args.clientId,
            "X-JAUTHTOKEN": args.pxpAuthToken,
            "X-LBTOKEN": args.userEmail,
        });
        const objects = response.data?.replyContent?.objects;
        return Array.isArray(objects) && objects.length > 0;
    } catch (e) {
        // Return null on failure so callers can skip updating hasVocabTraining
        // instead of regressing it to false (which would re-trigger onboarding).
        if (cp.cfg.ENVIRONMENT === "staging") {
            console.log("Error loading vocab trainings for onboarding flag", e);
        }
        return null;
    }
};

function useGeneralContext() {
    const context = React.useContext(AppGeneralContext);

    if (!context) {
        throw new Error("useGeneralContext must be used inside the provider");
    }

    let history = useHistory();
    const [cookies, setCookie, removeCookie] = useCookies(["JOSSO_SESSIONID"]);

    const {
        userId,
        setUserId,
        successfulAuth,
        setSuccessfulAuth,
        pxpAuthToken,
        clientId,
        setUserEmail,
        userEmail,
        setPxpAuthToken,
        setClientId,
        setJossoSessionId,
        setUserInfo,
        setTeacherInfo,
        setAvatarUrl,
        setHasVocabTraining,
        setHasTest,
    } = context;

    const setStateDataFromPXPJossoResponse = (pxpResponse: PXPJossoInfoResponse) => {
        const sessionToken = pxpResponse.replyContent?.p6pSessionToken;
        const userEmail = (pxpResponse.replyContent?.email || "").toLowerCase();
        const userId = pxpResponse.replyContent?.userDnsId;
        const jossoSessionId = pxpResponse.replyContent?.jossoSessionId;

        if (sessionToken && userEmail && userId && jossoSessionId) {
            setPxpAuthToken(sessionToken);
            setUserEmail(userEmail);
            setUserId(userId);
            setJossoSessionCookie(jossoSessionId);
        } else if (!sessionToken || !userId || !jossoSessionId) {
            throw new Error("No session nor user id info.");
        }
    };

    const _loadAndSetTeacherData = async (userInfo: UserInfo | undefined, pxpTokens: PxpSessionTokens | null) => {
        if (!_isTeacherRole(userInfo)) return;
        await getAndSetTeacherInfo();
        if (!pxpTokens) return;
        const loadedHasVocabTraining = await _loadHasVocabTraining(pxpTokens);
        if (loadedHasVocabTraining !== null) {
            setHasVocabTraining(loadedHasVocabTraining);
        }
    };

    const _fetchAndSetSessionInfo = async (sessionId: string): Promise<PXPJossoInfoResponse> => {
        const sessionInfo = await _fetchSessionInfo(sessionId);
        setStateDataFromPXPJossoResponse(sessionInfo);
        return sessionInfo;
    };

    async function connectToSession(sessionId?: string): Promise<PxpSessionTokens> {
        if (!sessionId) {
            throw new Error("Session id is missing");
        }

        const clientId = generateUuid();
        setClientId(clientId);

        const pxpData = await _fetchAndSetSessionInfo(sessionId);

        return {
            clientId,
            pxpAuthToken: pxpData.replyContent?.p6pSessionToken ?? "",
            userEmail: (pxpData.replyContent?.email ?? "").toLowerCase(),
            userId: pxpData.replyContent?.userDnsId ?? "",
        };
    }

    const getAndSetAvatar = async () => {
        try {
            const avatarResponse = await getRequest("/user/avatar/");

            const avatarUrl = avatarResponse?.data?.avatarUrl ?? getAvatarUrlFallback();

            setAvatarUrl(avatarUrl);
            return avatarUrl;
        } catch (error) {
            return "";
        }
    };

    const getAndSetUserInfo = async () => {
        try {
            const getUserInfoResponse: AxiosResponse<UserInfo> = await getRequest("aea/user/user-info/");
            setUserInfo(getUserInfoResponse.data);
            getUserInfoResponse.data.userEmail && setUserEmail(getUserInfoResponse.data.userEmail);
            setSentryRole(getUserInfoResponse.data.visitorOrUserRole);
            return Promise.resolve(getUserInfoResponse.data);
        } catch (e: any) {
            return Promise.reject(e.response.status);
        }
    };

    const getAndSetTeacherInfo = async () => {
        try {
            const teacherInfoResponse: AxiosResponse<TeacherResponse> = await getRequest("aea/teacher/teacher-info/");
            const teacherInfo = _normalizeTeacherInfo(teacherInfoResponse.data);
            setTeacherInfo(teacherInfo);

            return Promise.resolve(teacherInfo);
        } catch (e: any) {
            return Promise.reject(e.message);
        }
    };

    const setAnonymousUser = () => {
        setUserInfo({
            isAnonymousUser: true,
            userName: "",
            userEmail: "",
            visitorOrUserRole: "other",
            isMobileApp: false,
            isTeacherVerified: false,
            isInternalUser: false,
        });

        setTeacherInfo(null);
        setUserId("");
        setSentryRole(undefined);
        setAvatarUrl(getAvatarUrlFallback());
        setHasVocabTraining(false);
        setHasTest(false);
    };

    const getAndSetUserAndTeacherInfo = async () => {
        // User is already authenticated by establishSession or loginUser. Re-running would hit the stale `cookies`
        // state below and log the user straight back out via the catch.
        if (successfulAuth && !!userId) return;

        try {
            if (!cookies?.JOSSO_SESSIONID) throw new Error("User is not logged in");

            const userInfo = await getAndSetUserInfo();
            setHasTest(!!userInfo?.hasTest);

            // Establish pxp connection to set userId (needed for loading user tests in /assistent)
            let pxpTokens: PxpSessionTokens | null = null;
            if (userInfo?.userEmail) {
                pxpTokens = await connectToSession(cookies?.JOSSO_SESSIONID);
            }

            // Must precede the teacher-data await: the overview is clickable by now, and PrivateRoute
            // sends /create/* to /unauthorized while this is false.
            setSuccessfulAuth(true);

            if (userInfo) {
                getAndSetAvatar();
            }

            await _loadAndSetTeacherData(userInfo, pxpTokens);
        } catch (error) {
            setSuccessfulAuth(false);
            setAnonymousUser();
        }
    };

    /**
     * Logs the user in from a session id we already hold, rather than from the JOSSO cookie the browser
     * arrived with. Registration returns one (see registerUser), and without this the teacher stays
     * anonymous right after creating an account, so AnonymousTestClaimer never links their test.
     *
     * Takes the id as an argument instead of reading `cookies` back: setJossoSessionCookie updates React
     * state, so the value would still be stale within this same tick.
     *
     * @returns the user id of the session, for callers that act on it before React state has caught up.
     * Never empty: connectToSession throws when the session info carries no user id.
     */
    const establishSession = async (jossoSessionId: string): Promise<string> => {
        setJossoSessionCookie(jossoSessionId);

        const pxpTokens = await connectToSession(jossoSessionId);

        const userInfo: UserInfo = await getAndSetUserInfo();
        setHasTest(!!userInfo?.hasTest);
        setSuccessfulAuth(true);

        // Both run after the flip so the auth gap stays as short as possible. The avatar never gates
        // rendering; the teacher data is still awaited because callers navigate to /assistent right after.
        getAndSetAvatar();
        await _loadAndSetTeacherData(userInfo, pxpTokens);

        return pxpTokens.userId;
    };

    /**
     * Logs in from the JOSSO cookie. User-info loads before the session: connectToSession sets userId, and
     * AnonymousTestClaimer must not see that while userInfo is still empty.
     */
    const _loginFromCookie = async () => {
        const userInfo: UserInfo = await getAndSetUserInfo();
        setHasTest(!!userInfo?.hasTest);

        const pxpTokens = await connectToSession(cookies?.JOSSO_SESSIONID);

        if (userInfo) {
            getAndSetAvatar();
        }

        await _loadAndSetTeacherData(userInfo, pxpTokens);

        setSuccessfulAuth(true);
    };

    const loginUser = async (redirectTo: string) => {
        await _loginFromCookie();

        // If redirectTo starts with "/", treat it as a full path
        // Otherwise, append it to "/create" for backwards compatibility
        if (redirectTo.startsWith("/")) {
            history.push(redirectTo);
        } else {
            history.push("/create" + (redirectTo ? "/" + redirectTo : ""));
        }
    };

    /**
     * Login on the return from Google/Apple/fobizz (see buildSsoRegisterUrl). The Google/Apple chain sets the JOSSO
     * cookie but no contact group, so a new account gets the teacher role before the login: teacher data only loads
     * for teachers. fobizz's partner login already gives a new account the teacher role, so there only the
     * registration day tells it apart (date-only, so a second round trip on the same day counts as new too).
     */
    const loginUserAfterSso = async (provider: SsoProvider) => {
        const sessionInfo = await _fetchSessionInfo(cookies?.JOSSO_SESSIONID);
        const { roles: rawRoles = [], registrationDate, serverTime } = sessionInfo.replyContent ?? {};
        const roles = rawRoles.map((role) => role.toLowerCase());
        const needsTeacherRole = !hasAllowedRole(roles, STATUS_ROLES);
        // serverTime rather than the browser's clock, which may be off or in another timezone.
        const isNewFobizzAccount =
            provider === "fobizz" && !!registrationDate && registrationDate.slice(0, 10) === serverTime?.slice(0, 10);
        const isNewUser = needsTeacherRole || isNewFobizzAccount;

        if (needsTeacherRole) {
            // The account is usable without the role, so a failure is reported but doesn't block the login.
            await setUserGroup(TEACHER_CONTACT_GROUP).catch((error) => {
                Sentry.captureException(error, { extra: { stage: "setUserGroup after SSO", provider } });
            });
        }

        await _loginFromCookie();

        const handoff: AssistantHandoffState | undefined = isNewUser
            ? { ssoAccountCreatedProvider: provider }
            : undefined;
        history.push("/assistent", handoff);
    };

    const logoutUser = async () => {
        const jossoSessionId = cookies.JOSSO_SESSIONID;

        if (jossoSessionId) {
            try {
                await sessionGetRequest("/logout", { sessionId: jossoSessionId });
            } catch (error) {
                console.error("Error invalidating session");
            }
        }

        removeCookie("JOSSO_SESSIONID", {
            path: "/",
            domain: cp.cfg.REACT_APP_COOKIE_URL,
            secure: true,
        });

        setAnonymousUser();
        history.push("/assistent");
    };

    const setJossoSessionCookie = (jossoSessionId: string) => {
        const jossoSessionCookieVal = cookies.JOSSO_SESSIONID || "";
        if (jossoSessionId && jossoSessionId !== jossoSessionCookieVal) {
            setJossoSessionId(jossoSessionId);
            const cookieOptions: CookieSetOptions = {
                expires: moment().add(3, "months").toDate(),
                // Without this the cookie is scoped to the directory that happened to be open when it was
                // written, so it is never sent to /api/* and every call 403s. Previously invisible: in the
                // JOSSO redirect flow the server has already set the cookie at "/", making this a no-op.
                // logoutUser has always removed it with path "/", so this matches.
                path: "/",
            };
            setCookie("JOSSO_SESSIONID", jossoSessionId, cookieOptions);
        }
    };

    async function makePxpRequest(method: "GET" | "POST" | "DELETE", endpoint: string, params?: {}) {
        if (!userEmail || !pxpAuthToken) {
            return Promise.reject("Auth data needs to contain user email and auth token.");
        }
        const headers = {
            "X-CLIENTID": clientId || generateUuid(),
            "X-JAUTHTOKEN": pxpAuthToken,
            "X-LBTOKEN": userEmail,
        };

        if (method === "DELETE") {
            return pxpDeleteRequest(endpoint, headers);
        } else if (method === "GET") {
            return pxpGetRequest(endpoint, headers, params);
        } else {
            return pxpPostRequest(endpoint, headers, params);
        }
    }

    return {
        ...context,
        userId,
        setUserId,
        successfulAuth,
        setSuccessfulAuth,
        loginUser,
        loginUserAfterSso,
        establishSession,
        logoutUser,
        getAndSetUserAndTeacherInfo,
        makePxpRequest,
        getAndSetUserInfo,
        getAndSetTeacherInfo,
    };
}

function AppGeneralContextProvider(props: any) {
    const [userInfo, setUserInfo] = useState<UserInfo>({});
    const [teacherInfo, setTeacherInfo] = useState<TeacherResponse | null>(null);
    const [avatarUrl, setAvatarUrl] = useState<string>(getAvatarUrlFallback());

    const [userId, setUserId] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [successfulAuth, setSuccessfulAuth] = useState(false);
    const [pxpAuthToken, setPxpAuthToken] = useState("");

    const [isIframeMode, setIsIframeMode] = useState(false);

    const [clientId, setClientId] = useState("");
    const [jossoSessionId, setJossoSessionId] = useState("");
    const [isDataBeingLoaded, setIsDataBeingLoaded] = useState(false);
    const [creatingMode, setCreatingMode] = useState<CreationModes | "">("");
    const [creatingForSchoolClassId, setCreatingForSchoolClassId] = useState("");

    // Anonymous users arriving via the teacher area / assistant overview get special treatment (e.g. close creation to return to area)
    const [isAnonFromTeacherArea, setIsAnonFromTeacherArea] = useState(false);

    const [hasVocabTraining, setHasVocabTraining] = useState<boolean | null>(null);
    const [hasTest, setHasTest] = useState(false);

    const value = {
        userInfo,
        setUserInfo,
        teacherInfo,
        setTeacherInfo,
        avatarUrl,
        setAvatarUrl,
        userId,
        setUserId,
        successfulAuth,
        setSuccessfulAuth,
        clientId,
        setClientId,
        jossoSessionId,
        setJossoSessionId,
        isDataBeingLoaded,
        setIsDataBeingLoaded,
        isIframeMode,
        setIsIframeMode,
        creatingMode,
        setCreatingMode,
        userEmail,
        setUserEmail,
        pxpAuthToken,
        setPxpAuthToken,
        creatingForSchoolClassId,
        setCreatingForSchoolClassId,
        isAnonFromTeacherArea,
        setIsAnonFromTeacherArea,
        hasVocabTraining,
        setHasVocabTraining,
        hasTest,
        setHasTest,
    };

    return (
        <AppGeneralContext.Provider
            value={value}
            {...props}
        />
    );
}

export { AppGeneralContextProvider, useGeneralContext };
